import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Lock, Unlock } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { FontFamily } from '@/store/useUIStore';
import { cn } from '@/lib/utils';
import { ColorSwatch } from './ColorSwatch';
import { SliderControl } from './SliderControl';

const FONT_FAMILIES = [
    { id: 'Inter', name: 'Inter' },
    { id: 'Roboto', name: 'Roboto' },
    { id: 'Open Sans', name: 'Open Sans' },
    { id: 'Lato', name: 'Lato' },
    { id: 'Poppins', name: 'Poppins' },
    { id: 'Montserrat', name: 'Montserrat' },
    { id: 'Nunito', name: 'Nunito' },
    { id: 'Source Sans Pro', name: 'Source Sans Pro' },
    { id: 'Ubuntu', name: 'Ubuntu' },
    { id: 'Oxygen', name: 'Oxygen' },
    { id: 'Playfair Display', name: 'Playfair Display' },
    { id: 'Raleway', name: 'Raleway' },
    { id: 'Oswald', name: 'Oswald' },
    { id: 'Merriweather', name: 'Merriweather' },
    { id: 'Roboto Slab', name: 'Roboto Slab' },
    { id: 'Fira Sans', name: 'Fira Sans' },
    { id: 'Quicksand', name: 'Quicksand' },
    { id: 'Work Sans', name: 'Work Sans' },
    { id: 'Karla', name: 'Karla' },
    { id: 'Bebas Neue', name: 'Bebas Neue' },
    { id: 'Cinzel', name: 'Cinzel' },
    { id: 'Dancing Script', name: 'Dancing Script' },
    { id: 'Indie Flower', name: 'Indie Flower' },
    { id: 'Josefin Sans', name: 'Josefin Sans' },
    { id: 'Pacifico', name: 'Pacifico' },
    { id: 'Righteous', name: 'Righteous' },
    { id: 'Satisfy', name: 'Satisfy' }
] as const;

type ThemeTabProps = {
    fontFamily: FontFamily;
    onFontFamilyChange: (font: FontFamily) => void;

    fontSize: number;
    onFontSizeChange: (size: number) => void;

    // Navigation colors
    isNavColorsOpen: boolean;
    onNavColorsToggle: () => void;
    navColorsLocked: boolean;
    onNavColorsLockToggle: () => void;
    tempTopNavColor: string;
    tempSidebarColor: string;
    onTopNavColorClick: () => void;
    onSidebarColorClick: () => void;

    // UI and Text colors
    tempUiColor: string;
    tempTextColor: string;
    onUiColorClick: () => void;
    onTextColorClick: () => void;

    // Container colors
    isContainerColorsOpen: boolean;
    onContainerColorsToggle: () => void;
    containerColorsLocked: boolean;
    onContainerColorsLockToggle: () => void;
    tempContainerColor900: string;
    tempContainerColor800: string;
    tempContainerColor700: string;
    tempContainerColor600: string;
    onContainerColor900Click: () => void;
    onContainerColor800Click: () => void;
    onContainerColor700Click: () => void;
    onContainerColor600Click: () => void;

    // Container transparency
    containerTransparency: number;
    onContainerTransparencyChange: (value: number) => void;

    // Color picker
    activeColorPicker: 'background' | 'secondary' | 'topNav' | 'sidebar' | 'ui' | 'text';
    onColorChange: (color: string) => void;
    onMouseDown: () => void;
    onMouseUp: () => void;
    onTouchEnd: () => void;
    activeContainerPicker: '900' | '800' | '700' | '600';
};

export const ThemeTab: React.FC<ThemeTabProps> = ({
    fontFamily,
    onFontFamilyChange,
    fontSize,
    onFontSizeChange,
    isNavColorsOpen,
    onNavColorsToggle,
    navColorsLocked,
    onNavColorsLockToggle,
    tempTopNavColor,
    tempSidebarColor,
    onTopNavColorClick,
    onSidebarColorClick,
    tempUiColor,
    tempTextColor,
    onUiColorClick,
    onTextColorClick,
    isContainerColorsOpen,
    onContainerColorsToggle,
    containerColorsLocked,
    onContainerColorsLockToggle,
    tempContainerColor900,
    tempContainerColor800,
    tempContainerColor700,
    tempContainerColor600,
    onContainerColor900Click,
    onContainerColor800Click,
    onContainerColor700Click,
    onContainerColor600Click,
    containerTransparency,
    onContainerTransparencyChange,
    activeColorPicker,
    onColorChange,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    activeContainerPicker
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    return (
        <div className="p-2 space-y-3">
        {/* Font Family Selection */}
        <div>
            <div className="text-xs text-zinc-400 mb-2">Font Family</div>
            <div className="relative">
                <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-300 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent-1))]/50 flex items-center justify-between"
                >
                    <span className="font-preview" style={{ '--preview-font': fontFamily } as React.CSSProperties}>{fontFamily}</span>
                    <ChevronDown className="h-4 w-4" />
                </button>
                {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-700 rounded-lg z-10 max-h-60 overflow-y-auto">
                        {FONT_FAMILIES.map((font, index) => (
                            <div
                                key={font.id}
                                onClick={() => {
                                    onFontFamilyChange(font.id as FontFamily);
                                    setIsDropdownOpen(false);
                                }}
                                className={`px-3 py-1 text-base text-zinc-300 hover:bg-zinc-700 cursor-pointer font-preview text-center ${index < FONT_FAMILIES.length - 1 ? 'border-b border-zinc-500' : ''}`}
                                style={{ '--preview-font': font.id } as React.CSSProperties}
                            >
                                {font.name}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>

        {/* Font Size */}
        <SliderControl
            label="Font Size"
            value={fontSize}
            min={100}
            max={200}
            step={5}
            onChange={onFontSizeChange}
            formatValue={(v) => Math.round(v) + '%'}
        />

        {/* Color Controls */}
        <div className="space-y-3">
            {/* Navigation Colors Section */}
            <div className="space-y-3">
                <button
                    onClick={onNavColorsToggle}
                    className="w-full p-3 flex items-center justify-between text-xs text-zinc-400 hover:text-zinc-300"
                >
                    <div className="flex items-center justify-between w-full">
                        <span className="font-medium">Navigation Colors</span>
                        <div className="flex items-center gap-2">
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNavColorsLockToggle();
                                }}
                                className={cn(
                                    "p-1 rounded transition-colors cursor-pointer",
                                    navColorsLocked ? "text-[rgb(var(--accent-1))]" : "text-zinc-500 hover:text-zinc-400"
                                )}
                                title={navColorsLocked ? "Unlock navigation colors" : "Lock navigation colors together"}
                            >
                                {navColorsLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                            </div>
                            {isNavColorsOpen ? (
                                <ChevronUp className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                        </div>
                    </div>
                </button>

                {/* Collapsible Navigation Colors */}
                {isNavColorsOpen && (
                    <div className="space-y-3 px-3 pb-3">
                        {/* Top Nav Color */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-zinc-400">Top Navigation</span>
                                <ColorSwatch
                                    color={tempTopNavColor}
                                    isActive={activeColorPicker === 'topNav'}
                                    onClick={onTopNavColorClick}
                                    title="Click to edit this color"
                                />
                            </div>
                        </div>

                        {/* Sidebar Color */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-zinc-400">Sidebar</span>
                                <ColorSwatch
                                    color={tempSidebarColor}
                                    isActive={activeColorPicker === 'sidebar'}
                                    onClick={onSidebarColorClick}
                                    title="Click to edit this color"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* UI Color */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-400">UI Elements</span>
                    <ColorSwatch
                        color={tempUiColor}
                        isActive={activeColorPicker === 'ui'}
                        onClick={onUiColorClick}
                        title="Click to edit the color of UI containers and elements"
                    />
                </div>
            </div>

            {/* Text Color */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-400">Text Color</span>
                    <ColorSwatch
                        color={tempTextColor}
                        isActive={activeColorPicker === 'text'}
                        onClick={onTextColorClick}
                        title="Click to edit this color"
                    />
                </div>
            </div>
        </div>

        {/* Container Colors Section */}
        <div className="space-y-3">
            <button
                onClick={onContainerColorsToggle}
                className="w-full p-3 flex items-center justify-between text-xs text-zinc-400 hover:text-zinc-300"
            >
                <div className="flex items-center justify-between w-full">
                    <span className="font-medium">Container Colors</span>
                    <div className="flex items-center gap-2">
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onContainerColorsLockToggle();
                            }}
                            className={cn(
                                "p-1 rounded transition-colors cursor-pointer",
                                containerColorsLocked ? "text-[rgb(var(--accent-1))]" : "text-zinc-500 hover:text-zinc-400"
                            )}
                            title={containerColorsLocked ? "Unlock container colors" : "Lock container colors together"}
                        >
                            {containerColorsLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                        </div>
                        {isContainerColorsOpen ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </div>
                </div>
            </button>

            {/* Collapsible Container Colors */}
            {isContainerColorsOpen && (
                <div className="space-y-3 px-3 pb-3">
                    {/* Container 900 Color */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-zinc-400">Container 900</span>
                            <ColorSwatch
                                color={tempContainerColor900}
                                isActive={activeContainerPicker === '900'}
                                onClick={onContainerColor900Click}
                                title="Click to edit container-900 color"
                            />
                        </div>
                    </div>

                    {/* Container 800 Color */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-zinc-400">Container 800</span>
                            <ColorSwatch
                                color={tempContainerColor800}
                                isActive={activeContainerPicker === '800'}
                                onClick={onContainerColor800Click}
                                title="Click to edit container-800 color"
                            />
                        </div>
                    </div>

                    {/* Container 700 Color */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-zinc-400">Container 700</span>
                            <ColorSwatch
                                color={tempContainerColor700}
                                isActive={activeContainerPicker === '700'}
                                onClick={onContainerColor700Click}
                                title="Click to edit container-700 color"
                            />
                        </div>
                    </div>

                    {/* Container 600 Color */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-zinc-400">Container 600</span>
                            <ColorSwatch
                                color={tempContainerColor600}
                                isActive={activeContainerPicker === '600'}
                                onClick={onContainerColor600Click}
                                title="Click to edit container-600 color"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* Container Transparency */}
        <SliderControl
            label="Container Transparency"
            value={containerTransparency}
            min={0.1}
            max={1.0}
            step={0.05}
            onChange={onContainerTransparencyChange}
        />

        {/* Color Picker for Theme Colors */}
        <div className="flex justify-center pt-2 border-t border-zinc-700">
            <HexColorPicker
                color={
                    activeColorPicker === 'topNav' ? tempTopNavColor :
                        activeColorPicker === 'sidebar' ? tempSidebarColor :
                            activeColorPicker === 'ui' ? tempUiColor :
                                activeColorPicker === 'text' ? (
                                    activeContainerPicker === '900' ? tempContainerColor900 :
                                        activeContainerPicker === '800' ? tempContainerColor800 :
                                            activeContainerPicker === '700' ? tempContainerColor700 :
                                                activeContainerPicker === '600' ? tempContainerColor600 :
                                                    tempTextColor
                                ) :
                                    tempUiColor
                }
                onChange={onColorChange}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onTouchEnd={onTouchEnd}
                className="rounded-lg"
            />
        </div>
    </div>
    );
};